/*
 * Copyright © 2020-2025 XCamera. All Rights Reserved.
 * @Description:
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-11-10 14:14:54
 */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { Layout, Empty, Pagination, message, Button, Modal } from 'antd'
import Filter from './components/Filter'
import SafetyDocEntry from '../aiClerk/components/SafetyDocEntry'
import EditableTable from './components/EditableTable'
import ColumnsSetting from './components/ColumnsSetting'
import { useRecordsTableList } from './hooks/useRecordsTableList'
import type { XCAISecurityProps, XCAISecurityState } from './types'
import { XC_RECOGNITION_STATUS } from './types/contextInterface'
import type {
  RecognitionRecord,
  XCSelectionInfo
} from './types/contextInterface'
import { AISecurityProvider, useAISecurity } from './context/AISecurityContext'
import { getBaseColumnsData, getInitialBaseColumns } from './lib/columnConfigs'
import { waitingRecognitionInitialData } from './constants/initialFixedData'
import { useSelectedImage } from './hooks/useSelectedImage'
import { useUploadImage } from './hooks/useUploadImage'
import {
  useAppSelector,
  useAppDispatch,
  useVip,
  usePayment
} from '../../app/hooks'
import AIImageViewer from './components/AIImageViewer'
import ExportButton from './components/ExportButton'
import HeaderTitle from './components/HeaderTitle'
import { setPhotoPickerPhotos } from '../../actions/photoPicker'
import {
  useSensorTracking,
  generateBaseTrackingParams
} from './hooks/useSensorTracking'
import useTableRowInteraction from './hooks/useTableRowInteraction'
import useTableEditing from './hooks/useTableEditing'
import { useFeedbackModal } from './hooks/useFeedbackModal'
import './index.scss'
import { FeedbackModal } from '../aiRecognition/components'
import type { CheckboxChangeEvent } from 'antd/lib/checkbox'
import AntdProvider from '../../components/AntdProvider'

import PageHeader from '@/components/PageHeader'
const { Content } = Layout
const PAGE_SIZE = 50

/**
 * AI安全组件 - 照片台账表
 * @param props - 组件属性
 * @returns React函数组件
 */
const AISecurityContent: React.FC = () => {
  const dispatch = useAppDispatch()

  // 在组件顶层调用hooks
  const { isVip } = useVip()
  const { open } = usePayment()

  const {
    groupId,
    userId,
    setSelectedImages,
    filterParams,
    recognitionResult,
    setRecognitionStatus,
    isBatchMode
  } = useAISecurity()

  const {
    recognitionRecords,
    currentPage: hookCurrentPage,
    totalRecords,
    handlePageChange: hookPageChange,
    handleFetchRecords,
    addRecord,
    updateRecord,
    handleDownloadExcel,
    handleSaveRecord,
    handleBatchDeleteRecords,
    saving,
    exportingAll,
    exportingSelected
  } = useRecordsTableList({ initialPageSize: PAGE_SIZE })

  const {
    handleSelectPhotos,
    handleRecognition,
    handleRecognitionBatch,
    handleAgainGenerate,
    recognitionStatus
  } = useSelectedImage()
  const { reportAISecurityPageActionClick } = useSensorTracking()

  // 使用表格行交互检测hook
  const {
    checkInteractionState: checkFirstRowInteractionState,
    waitForInteractionEnd: waitForFirstRowInteractionEnd
  } = useTableRowInteraction()

  // 固定数据状态管理（移除status，使用context中的recognitionStatus）
  const [fixedDataState, setFixedDataState] = useState<RecognitionRecord>({
    ...waitingRecognitionInitialData
  })

  // 逐步显示AI识别结果的状态
  const [isShowingResults, setIsShowingResults] = useState(false)
  const [currentShowingIndex, setCurrentShowingIndex] = useState(-1)
  const [animatingRowIndex, setAnimatingRowIndex] = useState(-1)
  // 使用反馈Modal管理hook
  const {
    feedbackModalVisible,
    handleNegativeFeedback,
    handleFeedbackSubmit,
    handleFeedbackCancel
  } = useFeedbackModal()

  const selectedPhotos = useAppSelector((state) => {
    // 检查photoPicker中的from字段是否为ai-security
    if (state.photoPicker.from === 'ai-security') {
      return state.photoPicker.photos
    }

    // 否则返回空数组
    return []
  })

  // 页面进入埋点
  useEffect(() => {
    reportAISecurityPageActionClick({ actionType: 'enterPage' })
  }, [reportAISecurityPageActionClick])

  /**
   * 启动识别流程的公共函数
   * @param images - 图片URL数组
   * @param targetUserId - 目标用户ID
   * @param userNickname - 用户昵称
   * @param selectionInfo - 选择信息（可选）
   */
  const startRecognitionProcess = useCallback(
    (
      images: string[],
      targetUserId: string,
      userNickname: string,
      selectionInfo?: XCSelectionInfo[]
    ) => {
      // 更新固定行数据，显示选中的照片并设置为识别中状态
      setFixedDataState((prev) => ({
        ...prev,
        url: Array.isArray(images) ? images[0] : images,
        // 批量模式下用于固定行多图预览（最多3张）
        selectedPreviewImages: Array.isArray(images)
          ? images.slice(0, 5)
          : [images],
        batchModeTip: images.length > 1 ? '批量识别中' : undefined,
        nickName: userNickname,
        createTime: new Date().toLocaleString(),
        status: XC_RECOGNITION_STATUS.RECOGNIZING,
        isAnimationRow: true
      }))
      setSelectedImages(images)

      // 更新 context 中的识别状态
      setRecognitionStatus(XC_RECOGNITION_STATUS.RECOGNIZING)

      // 设置动画行索引为固定行（第0行）
      setAnimatingRowIndex(0)

      // 确保不在逐步显示结果状态，以便显示识别中动画
      setIsShowingResults(false)

      const isBatch = images.length > 1

      // 根据是否批量识别选择调用方式
      if (!isBatch) {
        // 单张识别：走单图识别流程，确保动画按之前逻辑展示
        handleRecognition(images, targetUserId, selectionInfo)
        return
      }

      // 批量识别（并发）
      handleRecognitionBatch(
        images,
        targetUserId,
        (response, index) => {
          const newRecord: RecognitionRecord = {
            ...waitingRecognitionInitialData,
            ...response,
            uuid:
              (response as any).uuid ||
              `${Date.now()}_${Math.random().toString(36).slice(2)}`,
            status: XC_RECOGNITION_STATUS.COMPLETED,
            isAnimationRow: false,
            isNewRecord: true
          }

          // 批量识别时，不执行动画，所有结果直接插入列表
          if (isBatch) {
            addRecord(newRecord)
            return
          }

          // 单图识别：第一个结果交给 useEffect 展示动画
          if (index === 0) return

          // 其他图片直接插入到列表首行（新记录动画）
          addRecord(newRecord)
        },
        selectionInfo
      )
    },
    [
      setFixedDataState,
      setSelectedImages,
      setRecognitionStatus,
      setAnimatingRowIndex,
      setIsShowingResults,
      handleRecognition,
      handleRecognitionBatch,
      addRecord
    ]
  )

  // 选择照片后，自动调用识别
  useEffect(() => {
    if (selectedPhotos.length > 0) {
      const images = selectedPhotos.map((photo) => photo.largeURL)
      const lastPhoto = selectedPhotos[selectedPhotos.length - 1]

      startRecognitionProcess(images, lastPhoto.userID, lastPhoto.userNickname)

      // 识别调用后需要重置state.photoPicker.photos
      dispatch(setPhotoPickerPhotos([]))
    }
  }, [selectedPhotos, startRecognitionProcess, dispatch])

  // 更新固定行数据的函数
  const updateFixedData = useCallback(
    (newData = {}) => {
      setFixedDataState((prev) => ({
        ...prev,
        ...newData
      }))
    },
    [handleFetchRecords, filterParams, reportAISecurityPageActionClick]
  )

  // 逐步显示AI识别结果的函数
  const showResultsGradually = useCallback(
    async (recognitionResult) => {
      setIsShowingResults(true)
      setCurrentShowingIndex(-1)
      setAnimatingRowIndex(0)

      // 需要逐步显示的字段列表（按顺序）
      const fieldsToShow = [
        { key: 'projectName', value: recognitionResult.projectName },
        { key: 'monomerName', value: recognitionResult.monomerName },
        {
          key: 'potentialHazardList',
          value: recognitionResult.potentialHazardList
        },
        { key: 'standardList', value: recognitionResult.standardList },
        {
          key: 'RectificationMeasure',
          value: recognitionResult.RectificationMeasure
        },
        { key: 'ConstructioUnit', value: recognitionResult.ConstructioUnit },
        { key: 'questionType', value: recognitionResult.questionType }
      ]

      // 逐个显示字段，每个间隔1秒
      for (let i = 0; i < fieldsToShow.length; i++) {
        const field = fieldsToShow[i]

        // 更新当前显示的字段索引
        setCurrentShowingIndex(i)

        // 等待800毫秒
        await new Promise((resolve) => setTimeout(resolve, 800))

        // 更新固定行数据，显示当前字段
        updateFixedData({
          [field.key]: field.value || ''
        })
      }

      setCurrentShowingIndex(10)
      setAnimatingRowIndex(-1)

      // // 所有字段显示完成后，先等待3秒
      await new Promise((resolve) => setTimeout(resolve, 3000))

      // 检测第一行是否处于交互状态，如果是则等待交互结束
      const { isHovering, isEditing } = checkFirstRowInteractionState()
      if (isHovering || isEditing) {
        await waitForFirstRowInteractionEnd()
      }

      // 使用函数式更新获取最新的fixedDataState
      let latestFixedDataState: RecognitionRecord = {}
      setFixedDataState((prev) => {
        latestFixedDataState = prev
        return prev
      })

      const newRecord = {
        ...latestFixedDataState,
        isAnimationRow: false,
        status: XC_RECOGNITION_STATUS.COMPLETED
      }

      // 将新记录插入到列表开头
      addRecord(newRecord)

      // 完全重置固定行数据为等待状态，确保清除所有之前的识别结果
      setFixedDataState({
        ...waitingRecognitionInitialData
      })

      // 重置识别状态为等待状态
      setRecognitionStatus(XC_RECOGNITION_STATUS.WAITING)
      setIsShowingResults(false)
      setCurrentShowingIndex(-1)
      setAnimatingRowIndex(-1)
    },
    [
      updateFixedData,
      addRecord,
      setRecognitionStatus,
      setCurrentShowingIndex,
      setAnimatingRowIndex,
      checkFirstRowInteractionState,
      waitForFirstRowInteractionEnd,
      fixedDataState
    ]
  )

  // 监听识别结果，当识别完成时处理结果
  useEffect(() => {
    if (
      recognitionStatus === XC_RECOGNITION_STATUS.COMPLETED &&
      recognitionResult &&
      !isShowingResults // 防止重复触发
    ) {
      // 批量识别：不执行动画，直接插入为新记录并复位固定行
      if (isBatchMode) {
        const newRecord: RecognitionRecord = {
          ...waitingRecognitionInitialData,
          ...recognitionResult,
          uuid:
            (recognitionResult as any).uuid ||
            `${Date.now()}_${Math.random().toString(36).slice(2)}`,
          status: XC_RECOGNITION_STATUS.COMPLETED,
          isAnimationRow: false,
          isNewRecord: true
        }
        addRecord(newRecord)

        // 复位到等待状态
        setFixedDataState({ ...waitingRecognitionInitialData })
        setRecognitionStatus(XC_RECOGNITION_STATUS.WAITING)
        setIsShowingResults(false)
        setCurrentShowingIndex(-1)
        setAnimatingRowIndex(-1)
        return
      }

      // 单图识别：更新固定行并执行逐步展示动画
      updateFixedData({
        id: (recognitionResult as any).id,
        ...(recognitionResult as any)
      })
      showResultsGradually(recognitionResult)
    }
  }, [
    recognitionStatus,
    recognitionResult,
    isShowingResults,
    showResultsGradually,
    isBatchMode
  ])

  // 获取初始列配置（优先从本地缓存读取）
  const getInitialCheckedColumns = useCallback(() => {
    try {
      const savedColumns = localStorage.getItem('aiSecurity-BaseColumns')
      if (savedColumns) {
        const parsedColumns = JSON.parse(savedColumns)
        if (Array.isArray(parsedColumns) && parsedColumns.length > 0) {
          // 确保 afterCorrectionPhoto 列始终被包含
          const defaultColumns = getInitialBaseColumns()
          const mergedColumns = Array.from(
            new Set([...parsedColumns, ...defaultColumns])
          )
          return mergedColumns
        }
      }
    } catch (error) {
      console.error('读取本地列配置失败:', error)
    }
    return getInitialBaseColumns()
  }, [])

  // 状态定义
  const [state, setState] = useState<XCAISecurityState>({
    checkedBaseColumns: getInitialCheckedColumns(),
    isSelectPreview: true
  })

  const [viewPhoto, setViewPhoto] = useState<RecognitionRecord>()
  const [userNickname, setUserNickname] = useState<string>()
  const [isViewerVisible, setIsViewerVisible] = useState(false)
  const [supportSelection, setSupportSelection] = useState(true)

  // 表格容器引用
  const tableWrapperRef = useRef<HTMLDivElement>(null)
  const editableTableRef = useRef<any>(null)

  // 图片上传处理
  const { getUploadState, handleChange, uploadImage } = useUploadImage(
    (record: RecognitionRecord, imageUrl: string) => {
      // 埋点：上传图片
      reportAISecurityPageActionClick({
        actionType: 'uploadPhotoFilesAfter',
        imageUrl
      })

      // 更新记录的整改后照片字段
      const updatedRecord = {
        ...record,
        photoFilesAfter: [imageUrl]
      }

      // 找到记录在数据源中的索引
      const recordIndex = dataSource.findIndex((item) => item.id === record.id)
      if (recordIndex !== -1) {
        // 更新数据源
        handleSaveRecord(updatedRecord, recordIndex)
      }
    }
  )

  /**
   * 本地上传处理函数
   * @param file - 上传的文件
   */
  const handleLocalUpload = useCallback(
    async (file: File) => {
      try {
        // 创建一个临时记录用于上传
        const tempRecord: RecognitionRecord = {
          ...fixedDataState,
          id: -999 // 使用负数作为临时ID
        }

        // 调用上传函数
        const result = await uploadImage(file, tempRecord)

        if (result.success && result.url) {
          // 上传成功后，设置图片并开始识别
          const images = [result.url]

          // 调用公共的识别启动函数
          startRecognitionProcess(images, userId ?? '', '')

          // 重置photoPicker状态
          dispatch(setPhotoPickerPhotos([]))

          message.success('图片上传成功，开始AI识别...')
        } else {
          message.error('图片上传失败，请重试')
        }
      } catch (error) {
        console.error('本地上传失败:', error)
        message.error('图片上传失败，请重试')
      }
    },
    [uploadImage, fixedDataState, startRecognitionProcess, userId, dispatch]
  )

  /**
   * 提交搜索
   */
  const handleSubmit = useCallback(() => {
    handleFetchRecords(1, PAGE_SIZE, filterParams)
  }, [handleFetchRecords, filterParams, groupId])

  /**
   * 重置搜索条件
   */
  const handleReset = useCallback(() => {
    handleFetchRecords(1, PAGE_SIZE, filterParams)
    // 上报导出表格埋点
    reportAISecurityPageActionClick({
      actionType: 'reset'
    })
  }, [])

  /**
   * 处理列设置变化
   * @param baseColumns - 基础列
   */
  const handleColumnsChange = useCallback((baseColumns: string[]) => {
    let finalBaseColumns = baseColumns
    setState((prevState) => ({
      ...prevState,
      checkedBaseColumns: finalBaseColumns
    }))
    localStorage.setItem(
      'aiSecurity-BaseColumns',
      JSON.stringify(finalBaseColumns)
    )
  }, [])

  /**
   * 处理选择/取消单条识别记录
   * @param record - 识别记录
   */
  const handleSelectRecognitionRecord = (record: RecognitionRecord) => {
    // 在当前页数据中找到该记录的行索引（包含第0行的固定数据）
    const rowIndex = dataSource.findIndex((item) => item.id === record.id)
    if (rowIndex !== -1) {
      const updatedRecord = { ...record, isSelected: !record.isSelected }
      // 使用 useRecordsTableList 提供的 updateRecord 来更新当前页数据
      updateRecord(rowIndex, updatedRecord)
    }
  }

  /**
   * 处理选择/取消全部识别记录
   */
  const handleSelectAllRecognitionRecords = (e: CheckboxChangeEvent) => {
    const isChecked = e.target.checked
    // 遍历当前页的识别记录（不包含第0行固定数据）
    recognitionRecords.forEach((record, idx) => {
      updateRecord(idx + 1, { ...record, isSelected: isChecked })
    })
  }

  /**
   * 查看照片
   * @param record - 照片记录
   * @param supportSelection - 是否支持图片框选功能
   */
  const handleViewPhoto = useCallback(
    (record: RecognitionRecord, supportSelection: boolean = true) => {
      // 埋点：点击照片
      reportAISecurityPageActionClick({
        actionType: 'clickOnePhoto',
        ...generateBaseTrackingParams(record)
      })

      // 直接传递图片URL字符串
      if (record.url) {
        setViewPhoto(record)
        setUserNickname(record.nickName)
        setSupportSelection(supportSelection)
        setIsViewerVisible(true)
      }
    },
    []
  )

  /**
   * 关闭图片查看器
   */
  const handleCloseViewer = useCallback(() => {
    setIsViewerVisible(false)
    setViewPhoto(undefined)
    setUserNickname(undefined)
    setSupportSelection(true)
  }, [])

  /**
   * 通用的字段动画显示逻辑
   * @param updatedRecord - 更新后的记录数据
   * @param rowIndex - 行索引
   */
  const executeFieldsAnimation = useCallback(
    async (updatedRecord: RecognitionRecord, rowIndex: number) => {
      // 需要逐步显示的字段列表（按columnConfigs中定义的fieldIndex顺序）
      const fieldsToShow = [
        { field: 'monomerName', fieldIndex: 0 },
        { field: 'potentialHazardList', fieldIndex: 1 },
        { field: 'standardList', fieldIndex: 2 },
        { field: 'RectificationMeasure', fieldIndex: 3 },
        { field: 'questionType', fieldIndex: 4 }
      ]

      // 统一的动画逻辑，支持任意行
      for (let i = 0; i < fieldsToShow.length; i++) {
        const { field, fieldIndex } = fieldsToShow[i]
        setCurrentShowingIndex(fieldIndex)
        setAnimatingRowIndex(rowIndex)

        await new Promise((resolve) => setTimeout(resolve, 300))

        // 获取字段值
        const fieldValue = updatedRecord[field as keyof RecognitionRecord]

        if (rowIndex === 0) {
          // 固定行使用 updateFixedData
          updateFixedData({
            ...updatedRecord,
            [field]: fieldValue || ''
          })
        } else {
          // 普通记录行使用 updateRecord
          const partialUpdate = {
            ...updatedRecord,
            [field]: fieldValue || ''
          }
          updateRecord(rowIndex, partialUpdate)
        }
      }

      setCurrentShowingIndex(10)
      setAnimatingRowIndex(10)

      // 最终更新完整记录，并重置动画状态
      const finalRecord = {
        ...updatedRecord,
        isAnimationRow: false // 动画完成后设置为false
      }

      if (rowIndex === 0) {
        updateFixedData(finalRecord)
      } else {
        updateRecord(rowIndex, finalRecord)
      }

      // 重置动画状态
      setIsShowingResults(false)
      setCurrentShowingIndex(-1)
      setAnimatingRowIndex(-1)
    },
    [updateRecord, updateFixedData, setIsShowingResults, setCurrentShowingIndex]
  )

  /**
   * 处理隐患生成来源模式变化
   * @param selectedIndex - 选择的索引
   * @param record - 记录数据
   * @param fieldName - 字段名
   * @param rowIndex - 行索引
   */
  const handleHazardSourceModeChange = useCallback(
    async (
      selectedIndex: number,
      record: RecognitionRecord,
      fieldName: string,
      rowIndex: number
    ) => {
      // 上报埋点
      reportAISecurityPageActionClick({
        actionType: 'hazardSourceModeChange',
        number1: record.hazardSourceList?.[selectedIndex]?.val,
        ...generateBaseTrackingParams(record)
      })

      // 立即更新记录数据，selectedHazardSource存储索引值
      const updatedRecord = {
        ...record,
        selectedHazardSource: selectedIndex,
        isAnimationRow: true // 动画期间设置为true
      }

      // 立即更新UI状态
      if (rowIndex === 0) {
        updateFixedData(updatedRecord)
      } else {
        updateRecord(rowIndex, updatedRecord)
      }

      // 开始动画效果
      setIsShowingResults(true)
      setCurrentShowingIndex(-1)
      setAnimatingRowIndex(rowIndex)

      // 异步保存到服务器
      handleSaveRecord(updatedRecord, rowIndex)

      // 执行字段动画
      await executeFieldsAnimation(updatedRecord, rowIndex)
    },
    [
      reportAISecurityPageActionClick,
      generateBaseTrackingParams,
      updateRecord,
      updateFixedData,
      handleSaveRecord,
      setIsShowingResults,
      setCurrentShowingIndex,
      setAnimatingRowIndex,
      executeFieldsAnimation
    ]
  )

  /**
   * 处理AI重新识别
   * @param record - 记录数据
   * @param fieldName - 字段名称
   * @param rowIndex - 行索引
   */
  const handleAgainGenerateClick = useCallback(
    async (record: RecognitionRecord, fieldName: string, rowIndex: number) => {
      // 非会员用户点击选择照片跳转开通会员
      if (!isVip) {
        open('ai-security')
        return
      }

      try {
        // 立即设置动画状态，让用户看到识别中动画
        setIsShowingResults(false)
        setCurrentShowingIndex(-1)
        setAnimatingRowIndex(rowIndex)

        // 更新记录状态为识别中
        const updatedStartRecord = {
          ...record,
          status: XC_RECOGNITION_STATUS.RECOGNIZING,
          isAnimationRow: true
        }
        updateRecord(rowIndex, updatedStartRecord)

        // 上报埋点
        reportAISecurityPageActionClick({
          actionType: 'aiReRecognition',
          ...generateBaseTrackingParams(record)
        })

        // 调用真正的AI重新识别API
        const recognitionResult = await handleAgainGenerate({
          id: record.id,
          descContent: record.potentialHazardList || [],
          url: record.url || '',
          groupID: groupId?.toString() || '',
          userID: userId || ''
        })

        console.log('recognitionResult------------', recognitionResult)

        if (recognitionResult) {
          // 开始动画效果
          setIsShowingResults(true)
          setCurrentShowingIndex(-1)
          setAnimatingRowIndex(rowIndex)

          // 更新记录数据，合并识别结果
          // 根据selectedHazardSource对应的name值决定更新哪个字段
          const hazardSourceArray = record.hazardSourceList || []
          const selectedIndex = record.selectedHazardSource ?? 0
          const selectedHazardSourceItem = hazardSourceArray[selectedIndex]
          const selectedHazardSourceName =
            selectedHazardSourceItem?.name || 'llm'

          let updatedRecord
          if (selectedHazardSourceName === 'watermark') {
            // 水印模式：只更新Content字段，保持普通字段不变
            updatedRecord = {
              ...record,
              status: XC_RECOGNITION_STATUS.COMPLETED,
              isAnimationRow: true, // 动画期间保持为true
              potentialHazardListContent:
                recognitionResult.potentialHazardList || [],
              standardListContent: recognitionResult.standardList || [],
              monomerNameContent: recognitionResult.monomerName || '',
              RectificationMeasureContent:
                recognitionResult.RectificationMeasure || '',
              questionTypeContent: recognitionResult.questionType || ''
            }
          } else {
            // LLM模式：只更新普通字段，保持Content字段不变
            updatedRecord = {
              ...record,
              ...recognitionResult,
              status: XC_RECOGNITION_STATUS.COMPLETED,
              isAnimationRow: true, // 动画期间保持为true
              potentialHazardListContent: record.potentialHazardListContent, // 保持原有的Content字段不变
              standardListContent: record.standardListContent,
              monomerNameContent: record.monomerNameContent,
              RectificationMeasureContent: record.RectificationMeasureContent,
              questionTypeContent: record.questionTypeContent
            }
          }
          updateRecord(rowIndex, updatedRecord)

          // 执行字段动画
          await executeFieldsAnimation(updatedRecord, rowIndex)
        } else {
          // 识别失败，恢复原状态
          const revertRecord = {
            ...record,
            status: XC_RECOGNITION_STATUS.COMPLETED,
            isAnimationRow: false
          }
          updateRecord(rowIndex, revertRecord)
        }
      } catch (error) {
        message.destroy()
        message.error('AI重新识别失败，请稍后重试')
        console.error('AI重新识别失败:', error)

        // 识别失败，恢复原状态
        const revertRecord = {
          ...record,
          status: XC_RECOGNITION_STATUS.COMPLETED,
          isAnimationRow: false
        }
        updateRecord(rowIndex, revertRecord)
      }
    },
    [
      reportAISecurityPageActionClick,
      generateBaseTrackingParams,
      handleAgainGenerate,
      groupId,
      userId,
      updateRecord,
      updateFixedData,
      setIsShowingResults,
      setCurrentShowingIndex,
      setAnimatingRowIndex,
      executeFieldsAnimation
    ]
  )

  // 使用表格编辑hook，包含自动重新识别功能
  const { handleEditStart, handleRecordSave } = useTableEditing({
    onSaveRecord: handleSaveRecord,
    onUpdateFixedData: updateFixedData,
    onReportAction: reportAISecurityPageActionClick,
    generateTrackingParams: generateBaseTrackingParams,
    onAgainGenerate: handleAgainGenerateClick
  })

  /**
   * 滑动到顶部
   */
  const scrollToTop = useCallback(() => {
    // 优先滚动EditableTable内部的表格到顶部
    if (editableTableRef.current && editableTableRef.current.scrollToTop) {
      editableTableRef.current.scrollToTop()
    }

    // 然后滚动到table-wrapper容器顶部
    if (tableWrapperRef.current) {
      tableWrapperRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    }
  }, [])

  /**
   * 开始识别处理
   */
  const handleStartRecognition = useCallback(
    (
      photo: string,
      selectionInfo: XCSelectionInfo[],
      userNickname?: string
    ) => {
      message.success('开始AI识别处理...')
      console.log('selectionInfo', selectionInfo)

      // 非会员用户点击选择照片跳转开通会员
      if (!isVip) {
        open('ai-security')
        return
      }

      // 滑动到顶部
      scrollToTop()

      // 使用公共函数启动识别流程
      startRecognitionProcess(
        [photo],
        userId ?? '',
        userNickname ?? '-',
        selectionInfo
      )
    },
    [userId, scrollToTop, startRecognitionProcess]
  )

  // 批量删除选中记录
  const handleBatchDeleteSelectedRecords = useCallback(() => {
    const selectedRecords = recognitionRecords.filter((r) => r.isSelected)
    if (selectedRecords.length === 0) return

    Modal.confirm({
      title: '确认删除选中记录',
      content: `将删除选中的 ${selectedRecords.length} 条记录，删除后不可恢复，是否继续？`,
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          // 使用新的异步批量删除函数
          const result = await handleBatchDeleteRecords(
            selectedRecords,
            reportAISecurityPageActionClick,
            generateBaseTrackingParams
          )

          // 根据结果显示详细反馈
          if (result.failedCount === 0) {
            message.success(`成功删除 ${result.successCount} 条记录`)

            recognitionRecords.length === 0 &&
              handleFetchRecords(1, PAGE_SIZE, filterParams)
          }
        } catch (err) {
          console.error('批量删除失败：', err)
          message.error('批量删除失败，请重试')
        }
      }
    })
  }, [
    recognitionRecords,
    handleBatchDeleteRecords,
    reportAISecurityPageActionClick
  ])

  /**
   * 数据源
   */
  const dataSource = useMemo(() => {
    // useExportList已经处理了分页，recognitionRecords是当前页的数据
    const sourceData = recognitionRecords.length > 0 ? recognitionRecords : []

    const dataWithFixed = [
      { ...fixedDataState, status: recognitionStatus, isAnimationRow: true },
      ...sourceData
    ]

    return dataWithFixed.map((item, index) => ({
      ...item,
      key: item.id || index
    }))
  }, [recognitionRecords, fixedDataState, recognitionStatus])

  const allSelected = useMemo(() => {
    return (
      recognitionRecords.length > 0 &&
      recognitionRecords.every((r) => r.isSelected)
    )
  }, [recognitionRecords])

  const someSelected = useMemo(() => {
    return recognitionRecords.some((r) => r.isSelected)
  }, [recognitionRecords])

  /**
   * 列配置
   */
  const columns = useMemo(() => {
    const filteredCols = getBaseColumnsData(
      () => {
        // 非会员用户点击选择照片跳转开通会员
        if (!isVip) {
          open('ai-security')
          return
        }
        handleSelectPhotos()
      },
      handleViewPhoto,
      isShowingResults,
      currentShowingIndex,
      {
        beforeUpload: () => {
          // 非会员用户点击选择照片跳转开通会员
          if (!isVip) {
            open('ai-security')
            return false
          }
          return true
        },
        handleChange,
        getUploadState,
        onLocalUpload: handleLocalUpload
      } as any,
      handleSelectRecognitionRecord,
      handleNegativeFeedback as any,
      animatingRowIndex,
      allSelected,
      someSelected,
      handleSelectAllRecognitionRecords
    )
      .filter((column) => state.checkedBaseColumns.includes(column.dataIndex))
      .map((column) => ({ ...column }))
    return filteredCols
  }, [
    state.checkedBaseColumns,
    handleViewPhoto,
    handleSelectPhotos,
    isShowingResults,
    currentShowingIndex,
    handleChange,
    getUploadState,
    handleLocalUpload,
    handleNegativeFeedback,
    animatingRowIndex
  ])

  const { checkedBaseColumns } = state
  return (
    <Layout className="aiSecurity-Data">
      <PageHeader
        ghost={false}
        style={{
          borderBottom: '1px solid rgb(235, 237, 240)',
          overflow: 'visible'
        }}
        title={<HeaderTitle text="AI安全隐患台账表" />}
        extra={[
          <ExportButton
            key="1"
            exporting={exportingAll}
            onClick={() => {
              if (exportingAll) return
              handleDownloadExcel({ ...filterParams, isAll: true })
            }}
            text="导出表格"
            loadingText="生成中"
          />
        ]}
      />
      <Content className="content">
        {/* AI 填表员 · 安全文书入口(可关闭,关闭后不再展示) */}
        <SafetyDocEntry />
        <div
          style={{
            flexDirection: 'row',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}
        >
          <div style={{ flex: 1, overflow: 'hidden' }}>
            {/* 添加一个包装div，控制溢出 */}
            <Filter
              dataSource={dataSource}
              onSubmit={handleSubmit}
              onReset={handleReset}
            />
          </div>
        </div>

        {dataSource.length ? (
          <div className="table-wrapper" ref={tableWrapperRef}>
            <div className="table-toolbar">
              <div className="toolbar-right">
                <ColumnsSetting
                  checkedBaseColumns={checkedBaseColumns}
                  onChange={handleColumnsChange}
                  handleSensorClickItem={() => {}}
                  isOpenNeteaseShelfReview={true}
                />
              </div>

              {/* 全选当前页面 */}
              <div className="toolbar-left">
                <span className="selected-count">
                  已选择{' '}
                  <strong>
                    {recognitionRecords.filter((r) => r.isSelected).length}
                  </strong>{' '}
                  条
                </span>

                <Button
                  onClick={handleBatchDeleteSelectedRecords}
                  disabled={
                    recognitionRecords.filter((r) => r.isSelected).length === 0
                  }
                >
                  删除选中
                </Button>

                <Button
                  type="primary"
                  onClick={() => {
                    if (exportingSelected) return
                    handleDownloadExcel({ ...filterParams, isAll: false })
                  }}
                  disabled={
                    recognitionRecords.filter((r) => r.isSelected).length === 0
                  }
                  loading={exportingSelected}
                >
                  导出选中
                </Button>
              </div>
            </div>

            <EditableTable
              ref={editableTableRef}
              dataSource={dataSource}
              columns={columns}
              onSave={handleRecordSave}
              onEditStart={handleEditStart}
              onAgainGenerate={handleAgainGenerateClick}
              onHazardSourceModeChange={handleHazardSourceModeChange}
              loading={saving}
            />
            <div className="pagination-wrapper">
              {/* 分页信息 */}
              <div className="pagination-section">
                <span className="total">共{totalRecords}条</span>
                <Pagination
                  current={hookCurrentPage}
                  total={totalRecords}
                  pageSize={PAGE_SIZE}
                  onChange={hookPageChange}
                  showSizeChanger={false}
                  showQuickJumper
                  showTotal={(total, range) =>
                    `第 ${range[0]}-${range[1]} 条/共 ${total} 条`
                  }
                />
              </div>
            </div>
          </div>
        ) : (
          <Empty style={{ marginTop: '300px' }} />
        )}
        {viewPhoto?.url && (
          <AIImageViewer
            visible={isViewerVisible}
            photo={viewPhoto.url}
            bbox={viewPhoto.bbox}
            userNickname={userNickname}
            onClose={handleCloseViewer}
            onStartRecognition={handleStartRecognition}
            supportSelection={supportSelection}
          />
        )}
      </Content>

      {/* 反馈Modal */}
      <FeedbackModal
        visible={feedbackModalVisible}
        onCancel={handleFeedbackCancel}
        onSubmit={(
          score: number,
          content: string,
          willPhoneCallback?: boolean
        ) => {
          // 使用hook的提交方法
          handleFeedbackSubmit({
            score,
            content,
            willPhoneCallback
          })
        }}
      />
    </Layout>
  )
}

/**
 * AI安全页面主组件
 * 注：XCAISecurityProps 接口保留用于类型兼容，但组件内部使用 context/hooks 获取所有状态
 */
const AISecurity: React.FC = React.memo(() => {
  return (
    <AntdProvider>
      <AISecurityProvider>
        <AISecurityContent />
      </AISecurityProvider>
    </AntdProvider>
  )
})

export default AISecurity
